import { useState, useEffect, useRef } from "react";
import { X, Lock, Shield } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

/**
 * PinModal — dual-mode dialog:
 *  - mode="set"    : First-time PIN setup or change (asks twice for confirm).
 *  - mode="verify" : Prompt for existing PIN before a transaction.
 *
 * Props:
 *  - mode: "set" | "verify"
 *  - title?: string
 *  - description?: string
 *  - onClose: () => void
 *  - onSuccess: (pin: string) => void  (verify: returns PIN; set: returns new PIN)
 *  - isChange?: boolean  (set mode — requires current PIN first if user already has one)
 */
export default function PinModal({ mode = "verify", title, description, onClose, onSuccess, isChange = false }) {
  const { markPinSet } = useAuth();
  const [step, setStep] = useState(mode === "set" && isChange ? "current" : mode === "set" ? "new" : "verify");
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const len6 = (s) => /^\d{6}$/.test(s);

  const submit = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      if (mode === "verify") {
        if (!len6(pin)) { toast.error("PIN 6 digit angka"); return; }
        onSuccess(pin);
        onClose();
        return;
      }
      // mode === "set"
      if (step === "current") {
        if (!len6(currentPin)) { toast.error("PIN lama 6 digit"); return; }
        // Verify current pin server-side first to avoid wasted UI
        await api.post("/auth/verify-pin", { current_pin: currentPin, new_pin: "000000" });
        setStep("new");
        return;
      }
      if (step === "new") {
        if (!len6(newPin)) { toast.error("PIN baru 6 digit angka"); return; }
        setStep("confirm");
        return;
      }
      if (step === "confirm") {
        if (newPin !== confirmPin) { toast.error("Konfirmasi PIN tidak cocok"); setConfirmPin(""); return; }
        const body = { new_pin: newPin };
        if (isChange) body.current_pin = currentPin;
        await api.post("/auth/set-pin", body);
        markPinSet?.();
        toast.success("PIN transaksi berhasil disetel");
        onSuccess(newPin);
        onClose();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal");
    } finally { setBusy(false); }
  };

  const valueFor = step === "current" ? currentPin : step === "new" ? newPin : step === "confirm" ? confirmPin : pin;
  const setterFor = step === "current" ? setCurrentPin : step === "new" ? setNewPin : step === "confirm" ? setConfirmPin : setPin;
  const labelFor = mode === "verify" ? "Masukkan PIN Transaksi"
    : step === "current" ? "PIN Lama"
    : step === "new" ? "PIN Baru (6 digit)"
    : "Konfirmasi PIN Baru";

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <form onSubmit={submit} className="bg-[#050505] border border-[#FFD700]/40 max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} data-testid="pin-modal">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#FFD700]/15 border border-[#FFD700]/40 flex items-center justify-center">
              {mode === "set" ? <Shield className="w-5 h-5 text-[#FFD700]" /> : <Lock className="w-5 h-5 text-[#FFD700]" />}
            </div>
            <div>
              <h3 className="font-display font-black text-xl text-white">{title || (mode === "set" ? "Setel PIN Transaksi" : "Konfirmasi PIN")}</h3>
              {description && <p className="text-xs text-white/60 mt-1">{description}</p>}
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={busy} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <label className="text-[10px] uppercase tracking-widest text-white/60">{labelFor}</label>
        <input
          data-testid="pin-input"
          autoFocus
          type="password"
          inputMode="numeric"
          pattern="\d{6}"
          maxLength={6}
          required
          value={valueFor}
          onChange={(e) => setterFor(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="w-full bg-black border border-white/15 focus:border-[#FFD700] text-white text-center text-2xl tracking-[1em] font-mono px-4 py-4 mt-2"
          placeholder="------"
        />

        {mode === "set" && (
          <div className="flex gap-1 mt-3">
            {["current", "new", "confirm"].filter((s) => isChange || s !== "current").map((s, i) => (
              <div key={s} className={`h-1 flex-1 ${
                (step === "current" && i === 0) || (step === "new" && (isChange ? i === 1 : i === 0)) || (step === "confirm" && (isChange ? i === 2 : i === 1))
                  ? "bg-[#FFD700]"
                  : ((step === "new" && i === 0) || (step === "confirm" && i < 2)) ? "bg-[#FFD700]/40" : "bg-white/15"
              }`} />
            ))}
          </div>
        )}

        <button data-testid="pin-submit" disabled={busy || !len6(valueFor)} type="submit" className="w-full mt-5 inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#FFD700] hover:bg-[#FFC700] text-black font-display font-black uppercase tracking-[0.15em] text-xs disabled:opacity-40">
          {busy ? "Memproses..." : mode === "verify" ? "Konfirmasi" : step === "confirm" ? "Simpan PIN" : "Lanjut"}
        </button>

        <p className="text-[10px] text-white/40 text-center mt-3">
          🔒 PIN ini akan diminta tiap transaksi (topup, bid auction, checkout). Jangan kasih ke siapa pun.
        </p>
      </form>
    </div>
  );
}
